import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link, useRouteMatch } from 'react-router-dom'
import useMediaQuery from '@/hooks/useMediaQuery'
import PropTypes from 'prop-types'
import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { useCableChannel } from '@/utils/cable'
import { MailIcon, PencilAltIcon, XIcon } from '@heroicons/react/solid'
import { FilterIcon } from '@heroicons/react/outline'
import useDropdown from '@/hooks/useDropdown'
import { InContentAd } from '@/Layout/Ads'
import Loading from '@/shared/Loading'

import PrivatePostShow from '@/pages/Posts/PrivatePostShow'
import PrivatePostForm from '@/pages/Posts/PrivatePostForm'
import PrivatePostPeopleInThisThread from '@/pages/Posts/PrivatePostPeopleInThisThread'
import MarkAllAsReadButton from '@/pages/Posts/MarkAllAsReadButton'
import { BellOffIcon } from '@/pages/Posts/NotifyLevelMenu'

// Discord-style messages: inbox on the left, the open thread in the middle,
// its people on the right (xl and up). Each pane scrolls itself; the page
// never does. Below md it's one pane at a time, inbox or thread.

const FILTER_LABELS = {
  all: 'All Messages',
  unread: 'Only Unread'
}

const NoResults = () => (
  <div className='m-3 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 p-6 text-center'>
    <MailIcon className='mx-auto h-10 w-10 text-gray-400' />
    <span className='mt-2 block text-sm font-medium text-gray-900 dark:text-gray-300'>No private messages here. Send a 'hello' to someone!</span>
    <Link to='/private_messages/new' className='inline-flex mt-3 px-4 py-2 text-sm font-medium rounded-md text-white bg-cccblue hover:bg-cccblue-alt'>
      New Private Message
    </Link>
  </div>
)

const PostRow = ({ post, selected, onOpen, level }) => (
  <li>
    <Link
      to={`/private_messages/${post.id}`}
      onClick={() => onOpen(post.id)}
      className={`flex items-center gap-2 mx-2 px-2 py-2 rounded-md text-gray-700 dark:text-gray-200 ${selected ? 'bg-gray-200 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-700/60'}`}
    >
      <div className='flex -space-x-2 shrink-0'>
        {post.postUsers.slice(0, 2).map((pu) => (
          <img key={`puuser${pu.id}`} className='h-8 w-8 rounded-full ring-2 ring-white dark:ring-gray-800 bg-gray-200 object-cover' src={pu.publicImageUrl} alt='' />
        ))}
      </div>
      <div className='min-w-0 flex-1'>
        <div className='flex items-baseline justify-between gap-2'>
          <span className={`truncate text-sm ${post.isRead ? '' : 'font-semibold text-gray-900 dark:text-white'}`}>{post.lastMessage.userDisplayName}</span>
          <span className='shrink-0 text-xs text-gray-400'>{post.timeAgo}</span>
        </div>
        <div className='truncate text-xs text-gray-500 dark:text-gray-400'>{post.lastMessage.lastMessagePreview}</div>
      </div>
      {level !== 'all' && <span title={level === 'none' ? 'Muted' : 'Mentions only'} className='shrink-0 text-gray-400'>
        {level === 'none' ? <BellOffIcon className='h-3.5 w-3.5' /> : <span className='text-xs font-bold'>@</span>}
      </span>}
      {!post.isRead && <span className='w-2 h-2 shrink-0 rounded-full bg-pink-600' aria-label='Unread' />}
    </Link>
  </li>
)

const Inbox = ({ openId, showAd, levels }) => {
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)
  const [end, setEnd] = useState(false)
  const [filter, setFilter] = useState('all')
  const startsAt = useRef(null)
  const busy = useRef(false)
  const dropdownRef = useRef(false)
  const { dropdownOpen, openDropdown, setDropdownOpen } = useDropdown(dropdownRef)
  const sentinel = useRef(null)
  const listRef = useRef(null)

  const fetchPage = useCallback((append) => {
    if (busy.current) return
    busy.current = true
    setLoading(true)
    const data = append ? { filter, ends_at: startsAt.current } : { filter }
    getRequest('/api/v3/posts/private', data, (err, jsonData) => {
      busy.current = false
      setLoading(false)
      if (err) return

      startsAt.current = jsonData.startsAt
      setEnd(jsonData.posts.length === 0)
      setPosts(prev => {
        if (!append) return jsonData.posts
        const known = new Set(prev.map(p => p.id))
        return [...prev, ...jsonData.posts.filter(p => !known.has(p.id))]
      })
    })
  }, [filter])

  // First page on mount and on every filter change. This used to race a
  // loadMore that also fired on mount, which could load the first page twice.
  useEffect(() => {
    setDropdownOpen(false)
    setEnd(false)
    setPosts([])
    fetchPage(false)
  }, [filter])

  // Further pages only once there is a first page to page from.
  useEffect(() => {
    const el = sentinel.current
    if (!el || end || posts.length === 0) return undefined
    const observer = new window.IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) fetchPage(true)
    }, { root: listRef.current })
    observer.observe(el)
    return () => observer.disconnect()
  }, [posts.length, end, fetchPage])

  // Opening a thread reads it.
  const markOpened = (id) => setPosts(prev => prev.map(p => (p.id === id ? { ...p, isRead: true } : p)))
  useEffect(() => { if (openId) markOpened(Number(openId)) }, [openId])

  // A thread we aren't holding (new, or further down the list): fetch page
  // one and lay it over the top of what's loaded, keeping every page the
  // reader already scrolled through. Not behind `busy`, so a page load in
  // flight can't swallow it.
  const pullNewest = () => {
    getRequest('/api/v3/posts/private', { filter }, (err, jsonData) => {
      if (err) return
      setPosts(prev => {
        const top = new Set(jsonData.posts.map(p => p.id))
        return [...jsonData.posts, ...prev.filter(p => !top.has(p.id))]
      })
    })
  }

  useCableChannel('PrivateMessagesChannel', (data) => {
    if (data.event !== 'created') return
    const postId = data.postId
    const mine = data.message.userId === currentUser?.id
    if (!posts.some(p => p.id === postId)) {
      // Under "Only Unread" your own message is in a thread the list leaves
      // out on purpose, so there's nothing to add. A thread you just started
      // under "All Messages" still needs pulling in.
      if (!mine || filter === 'all') pullNewest()
      return
    }
    setPosts(prev => {
      const post = prev.find(p => p.id === postId)
      const updated = {
        ...post,
        timeAgo: 'now',
        isRead: mine || String(postId) === String(openId),
        lastMessage: {
          ...post.lastMessage,
          userId: data.message.userId,
          userUsername: data.message.userUsername,
          userDisplayName: data.message.userDisplayName,
          lastMessagePreview: data.preview
        }
      }
      return [updated, ...prev.filter(p => p.id !== postId)]
    })
  })

  return <div className='flex-1 min-h-0 flex flex-col bg-gray-50 dark:bg-gray-800'>
    <div className='shrink-0 h-14 flex items-center justify-between gap-2 px-4 border-b border-gray-200 dark:border-gray-700'>
      <h1 className='font-semibold text-gray-900 dark:text-white'>Messages</h1>
      <div className='flex items-center'>
        <div className='relative'>
          <button onClick={openDropdown} className='flex items-center gap-1 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700'>
            <FilterIcon className='h-4 w-4' aria-hidden='true' />
            <span>{FILTER_LABELS[filter]}</span>
          </button>
          {dropdownOpen && <div ref={dropdownRef} tabIndex='-1' className='absolute right-0 z-30 top-7 py-2 bg-white border border-gray-900 dark:border-gray-100 rounded-md shadow-md dark:bg-gray-800 whitespace-nowrap'>
            {Object.entries(FILTER_LABELS).map(([key, label]) => (
              <div key={key} onClick={() => setFilter(key)} className='text-gray-700 dark:text-gray-100 px-4 py-2 text-sm cursor-pointer hover:text-cccblue-alt'>{label}</div>
            ))}
          </div>}
        </div>
        <MarkAllAsReadButton />
        <Link to='/private_messages/new' aria-label='New Private Message' title='New Private Message' className='p-1 text-gray-500 hover:text-cccblue dark:text-gray-300'>
          <PencilAltIcon className='h-5 w-5' />
        </Link>
      </div>
    </div>
    <ul ref={listRef} className='flex-1 min-h-0 overflow-y-auto py-2 space-y-0.5'>
      {posts.map((post) => (
        <PostRow key={`postrow${post.id}`} post={post} selected={String(post.id) === String(openId)} onOpen={markOpened} level={levels[post.id] || post.notifyLevel || 'all'} />
      ))}
      {loading && <li><Loading /></li>}
      {!loading && posts.length === 0 && <li><NoResults /></li>}
      {!end && <li ref={sentinel} className='h-px' />}
      {showAd && <li className='px-3 pb-3'><InContentAd /></li>}
    </ul>
  </div>
}

const EmptyThread = () => (
  <div className='flex-1 flex flex-col items-center justify-center p-8 text-center bg-white dark:bg-gray-800'>
    <MailIcon className='h-12 w-12 text-gray-300 dark:text-gray-600' />
    <p className='mt-3 text-sm text-gray-500 dark:text-gray-400'>Pick a conversation.</p>
  </div>
)

const PrivatePostsIndex = () => {
  const isMobile = !useMediaQuery('(min-width: 768px)')
  const isWide = useMediaQuery('(min-width: 1280px)')
  const threadMatch = useRouteMatch('/private_messages/:id(\\d+)')
  const isNew = !!useRouteMatch('/private_messages/new')
  const postId = threadMatch?.params.id
  const { getRequest } = useQuery()
  const [people, setPeople] = useState([])
  const [peopleLoading, setPeopleLoading] = useState(false)
  const [peopleOpen, setPeopleOpen] = useState(false)
  // Levels changed in the thread header this visit, so the inbox row's icon
  // follows without a refetch. { postId: level }
  const [levels, setLevels] = useState({})
  const onLevelChange = useCallback((id, level) => setLevels(prev => ({ ...prev, [id]: level })), [])

  // Fetched once here and shared: the conversation header names them, the
  // people panel lists them.
  useEffect(() => {
    setPeople([])
    setPeopleOpen(false)
    if (!postId) return
    setPeopleLoading(true)
    getRequest(`/api/v3/posts/${postId}/post_users`, {}, (err, jsonData) => {
      setPeopleLoading(false)
      if (err) return
      setPeople(jsonData.postUsers)
    })
  }, [postId])

  const center = postId
    ? <PrivatePostShow
        postId={postId}
        people={people}
        isMobile={isMobile}
        onShowPeople={isWide ? null : () => setPeopleOpen(true)}
        onLevelChange={onLevelChange}
      />
    : isNew
      ? <div className='flex-1 min-h-0 overflow-y-auto p-4 bg-white dark:bg-gray-800'><PrivatePostForm /></div>
      : <EmptyThread />

  const peoplePanel = postId && <PrivatePostPeopleInThisThread people={people} loading={peopleLoading} />

  if (isMobile) {
    return <div className='flex-1 min-h-0 flex flex-col'>
      {(postId || isNew) ? center : <Inbox openId={postId} showAd levels={levels} />}
      {peopleOpen && <PeopleDrawer onClose={() => setPeopleOpen(false)}>{peoplePanel}</PeopleDrawer>}
    </div>
  }

  return <div className='flex-1 min-h-0 flex'>
    <div className='w-72 lg:w-80 shrink-0 flex flex-col border-r border-gray-200 dark:border-gray-700'>
      <Inbox openId={postId} showAd={false} levels={levels} />
    </div>
    <div className='flex-1 min-w-0 flex flex-col'>{center}</div>
    {isWide && <div className='w-72 shrink-0 flex flex-col border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'>
      {peoplePanel || <div className='flex-1' />}
      {/* The open thread is ad-free (see AD_FREE_ROUTES in App.jsx), so the
          slot only exists when no thread is. Fixed size, reserved space. */}
      {!postId && <div className='shrink-0 p-4'><InContentAd /></div>}
    </div>}
    {!isWide && peopleOpen && <PeopleDrawer onClose={() => setPeopleOpen(false)}>{peoplePanel}</PeopleDrawer>}
  </div>
}

const PeopleDrawer = ({ onClose, children }) => (
  <div className='fixed inset-0 z-40 flex justify-end' role='dialog' aria-modal='true'>
    <div className='absolute inset-0 bg-black/40' onClick={onClose} />
    <div className='relative h-full w-[85%] max-w-xs flex flex-col bg-white dark:bg-gray-800 shadow-2xl'>
      <button type='button' onClick={onClose} aria-label='Close' className='absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-white'>
        <XIcon className='h-5 w-5' />
      </button>
      {children}
    </div>
  </div>
)

export default PrivatePostsIndex

PostRow.propTypes = {
  post: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  level: PropTypes.string
}
Inbox.propTypes = {
  openId: PropTypes.string,
  showAd: PropTypes.bool,
  levels: PropTypes.object
}
PeopleDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node
}
